import { leadChannel } from './types';
import { awaitingFirstTouch, leadsInStage } from './stageKeys';
import { activityDayIso } from './activityStats';
import { isTouchKind } from './leadActivity';
import { inRange } from './dashboardRange';
import type { ActivitySlim } from './activityStats';
import type { DateRange } from './dashboardRange';
import type { DealSlim } from './dashboardWork';
import type { LeadDealOutcome } from './dealOutcomes';
import type { Lead, LeadStage, Membership } from './types';

/**
 * The dashboard's three cohorts, and which of its boxes can be folded away.
 *
 * Kyu, 2026-08-26, looking at the live page: the lower boxes do not collapse,
 * and the top summary is not the three sections he asked for. Both had been
 * asked for before. The August work on this screen was model and honesty work
 * — "Not set" no longer counted as Outbound, Needs Review made visible — and
 * none of it changed the shape of the page, which is why it still reads as
 * untouched.
 */

/* ------------------------------------------------------------------ */
/* The three cohorts                                                    */
/* ------------------------------------------------------------------ */

export type LeadCohort = 'all' | 'inbound' | 'outbound';

export const COHORT_LABELS: Record<LeadCohort, string> = {
  all: 'All Leads',
  inbound: 'Inbound',
  outbound: 'Outbound',
};

/**
 * Membership goes through `leadChannel` and nothing else.
 *
 * This is the whole reason the cohorts are safe to add. `leadChannel` is the
 * single rule that decides what a lead's direction is, and it treats a null
 * source as neither inbound nor outbound. Writing `lead.source === 'outbound'`
 * here would work today and drift the first time that rule changes — and the
 * last time this screen had its own copy of the rule, it folded 443 blank
 * rows into outbound and reported 766 outbound leads when 98 were tagged.
 *
 * So an Outbound section can never quietly fill up with unclassified leads.
 * That is Kyu's standing instruction, enforced by construction rather than by
 * remembering.
 */
export function cohortLeads(leads: Lead[], cohort: LeadCohort): Lead[] {
  if (cohort === 'all') {
    return leads;
  }
  return leads.filter((lead) => leadChannel(lead) === cohort);
}

/* ------------------------------------------------------------------ */
/* Stock and flow, kept apart on purpose                                */
/* ------------------------------------------------------------------ */

/**
 * ── Why there are two of these instead of one ──────────────────────────────
 *
 * Kyu asked for time-range controls on the summary, and five of the six
 * figures that were here could not honestly take one.
 *
 * They are counts of where leads are RIGHT NOW, read off `leads.stage_id`.
 * The lead row carries created_at, updated_at, last_touch_at and
 * nurture_since — and no column at all recording when a lead entered a
 * status. `lead_stage_log` does log every stage move with a timestamp, but it
 * stores the status's DISPLAY NAME as it read at the time, across a
 * vocabulary rename (Cold / Nurture / Replied / In Talks / Active Deal / Won
 * before 20261122; New / Contacted / Engaged / Converted / Closed after), with
 * no id and no key to join on. Windowing "In Talks" through that means
 * matching a string somebody can rename in the UI — the exact mistake
 * stageKeys.ts was written to delete, and the one that once reported 766
 * outbound leads against 98 tagged.
 *
 * So the numbers are split by whether they have a real date behind them:
 *
 *   CohortFlow  — things that HAPPENED, each dated exactly. Ranged.
 *   CohortStock — where the pipeline stands. Labelled "now", never ranged.
 *
 * Kyu, 2026-08-27: "do not fake time-range movement on figures that are really
 * 'current state' counts … label the distinction clearly so the numbers stay
 * honest." The split is that instruction, made structural — a future figure
 * has to be put in one of these two, and putting a stock number in the flow
 * type does not typecheck into existence a date it does not have.
 */

/** Where this cohort's pipeline stands. No date anywhere in it. */
export interface CohortStock {
  total: number;
  awaitingFirstTouch: number;
  inTalks: number;
  activeDeals: number;
  /**
   * Leads whose deal was WON.
   *
   * REPLACED `closed` on 1 Sep. Kyu, looking at the tile: "let's change this to
   * WON, 'Closed' means both lost and won right? or whatever." It did not.
   * Closed is the lead STATUS meaning no longer pursued — far nearer to lost
   * than to won — so relabelling it would have turned nine abandoned leads into
   * nine wins on the top-line dashboard. Told him; he chose to swap the tile
   * rather than rename it.
   *
   * Won is not a lead status and cannot be one, so it is read off the linked
   * DEALS. One rule in one place — a Dashboard Won that disagreed with the
   * Pipeline's Won column would be worse than having no Won at all.
   *
   * WIDENED 2 Sep to "has ever won a deal", matching originBreakdown.ts. It
   * used to go through `leadPosition`, where open work outranks history, and
   * that made a returning customer stop counting as a win. See the comment in
   * the function body.
   */
  won: number;
  /**
   * Each tile's leads, so clicking it opens them. Every count above is the
   * LENGTH of its list here, never a second count of the same question, so a
   * tile cannot say one number and open another.
   */
  totalIds: readonly string[];
  awaitingFirstTouchIds: readonly string[];
  inTalksIds: readonly string[];
  activeDealsIds: readonly string[];
  wonIds: readonly string[];
  /**
   * The deal outcomes had not loaded, so `won` is 0 because it is UNKNOWN.
   * Never render that as a number: a Won tile reading 0 while the data is in
   * flight is a wrong answer, not a slow one.
   */
  wonUnknown: boolean;
}

/**
 * A slice of leads, as a count and the ids behind it.
 *
 * One helper rather than a count here and an id list there, because the whole
 * value of a tile you can click is that the number and the list agree. Derive
 * the first from the second and they cannot do otherwise.
 */
function slice(rows: Lead[], keep: (lead: Lead) => boolean): { count: number; ids: string[] } {
  const ids = rows.filter(keep).map((lead) => lead.id);
  return { count: ids.length, ids };
}

export function cohortStock(
  leads: Lead[],
  stages: LeadStage[],
  /** From `useLeadDealOutcomes`. undefined while it is still loading. */
  outcomes: ReadonlyMap<string, LeadDealOutcome> | undefined,
  cohort: LeadCohort,
): CohortStock {
  const rows = cohortLeads(leads, cohort);
  const engagedIds = new Set(leadsInStage(rows, stages, 'engaged').map((lead) => lead.id));
  const convertedIds = new Set(leadsInStage(rows, stages, 'converted').map((lead) => lead.id));
  const awaiting = slice(rows, (lead) => awaitingFirstTouch(lead, stages));
  const inTalks = slice(rows, (lead) => engagedIds.has(lead.id));
  const activeDeals = slice(rows, (lead) => convertedIds.has(lead.id));
  const wonSlice =
    outcomes === undefined
      ? { count: 0, ids: [] }
      : slice(rows, (lead) => (outcomes.get(lead.id)?.won_deals ?? 0) > 0);
  return {
    total: rows.length,
    totalIds: rows.map((lead) => lead.id),
    awaitingFirstTouch: awaiting.count,
    awaitingFirstTouchIds: awaiting.ids,
    inTalks: inTalks.count,
    inTalksIds: inTalks.ids,
    activeDeals: activeDeals.count,
    activeDealsIds: activeDeals.ids,
    wonIds: wonSlice.ids,
    // WIDENED 2 Sep, in step with the Pipeline's Won column. This read
    // `leadPosition(...) === 'won'`, where open work outranks history — so a
    // client who bought and then opened another deal stopped counting as a
    // win. Kyu: "all won deals, according to the timeline, should reflect to
    // conversion accordingly."
    //
    // A win is a fact about the deals, not a position the lead occupies. Both
    // screens now read it the same way, which is the whole reason this tile
    // was built off the shared rule in the first place.
    won: wonSlice.count,
    wonUnknown: outcomes === undefined,
  };
}

/** What happened to this cohort inside the selected range. Every figure dated. */
export interface CohortFlow {
  /** leads.created_at */
  arrived: number;
  /** lead_activity, the touch kinds */
  touches: number;
  /** lead_activity reply_received ∪ lead_list_memberships.reply_date */
  replies: number;
  /** lead_activity kind = meeting */
  meetings: number;
  /** deals.created_at. Zero and unknown look the same here — see the caller. */
  converted: number;
}

export function cohortFlow(
  leads: Lead[],
  activities: ActivitySlim[],
  memberships: Membership[],
  deals: DealSlim[],
  cohort: LeadCohort,
  range: DateRange,
): CohortFlow {
  const rows = cohortLeads(leads, cohort);
  // Membership goes through cohortLeads, so a lead with no source can never be
  // counted into Outbound by any figure below — the same guarantee the stock
  // numbers have, extended to the flow ones rather than re-derived.
  const mine = new Set(rows.map((lead) => lead.id));

  let touches = 0;
  let meetings = 0;
  // Replies are deduplicated by lead and day. THE REASON: a reply marked from
  // the Outreach grid writes `reply_date` on the membership and nothing on the
  // timeline, while a reply logged on the lead writes `reply_received` and
  // nothing on the membership. Counting the union without deduplicating means
  // one reply recorded both ways counts twice — and this figure sits directly
  // above a list of reply EVENTS that is deduplicated the same way, so a plain
  // sum would put a count and its own list in visible disagreement.
  //
  // Reports still sums the two sources (activityStats.repliesOn). That is a
  // real difference between two screens and it is written down in
  // docs/open-items.md rather than quietly reconciled here — Reports is a
  // working flow and changing what it counts is Kyu's call, not a side effect
  // of a dashboard pass.
  const replies = new Set<string>();

  for (const activity of activities) {
    if (!mine.has(activity.lead_id) || !inRange(activity.occurred_at, range)) {
      continue;
    }
    // A MEETING COUNTS IN BOTH, and that is not a bug to tidy up later.
    // isTouchKind mirrors the database function lead_activity_is_touch, where
    // a meeting is a touch. Excluding it here to make the two figures disjoint
    // would give this screen a private definition of "touch" that disagrees
    // with the Leads list, the funnel and the database. So Meetings is a
    // SUBSET of Touches, not a sibling of it.
    if (isTouchKind(activity.kind)) {
      touches += 1;
    }
    if (activity.kind === 'meeting') {
      meetings += 1;
    }
    if (activity.kind === 'reply_received') {
      replies.add(`${activity.lead_id}:${activityDayIso(activity)}`);
    }
  }

  for (const membership of memberships) {
    if (
      membership.reply_date !== null &&
      mine.has(membership.lead_id) &&
      inRange(membership.reply_date, range)
    ) {
      replies.add(`${membership.lead_id}:${membership.reply_date}`);
    }
  }

  return {
    arrived: rows.filter((lead) => inRange(lead.created_at, range)).length,
    touches,
    meetings,
    replies: replies.size,
    converted: deals.filter(
      (deal) => deal.lead_id !== null && mine.has(deal.lead_id) && inRange(deal.created_at, range),
    ).length,
  };
}

/* ------------------------------------------------------------------ */
/* Which boxes fold, and whether they remember                          */
/* ------------------------------------------------------------------ */

export interface DashboardSection {
  key: string;
  title: string;
}

/**
 * The seven lower boxes, in the order they appear.
 *
 * Kyu named four — Stage Funnel, Conversion Funnel, Copy Performance, Sends
 * per List — "and the rest of those lower dashboard boxes". These are the
 * rest. The Today strip and the three cohort sections are NOT here: they are
 * the summary, and a summary you have to unfold is not a summary.
 */
export const DASHBOARD_SECTIONS: DashboardSection[] = [
  { key: 'stage-funnel', title: 'Stage Funnel' },
  { key: 'copy-performance', title: 'Copy Performance' },
  { key: 'conversion-funnel', title: 'Conversion Funnel' },
  { key: 'sends-per-list', title: 'Sends Per List' },
  { key: 'daily-activity', title: 'Daily Activity (Last 14 Days)' },
  { key: 'recent-leads', title: 'Recent Leads' },
  { key: 'inbound-consent', title: 'Inbound Leads (Consent Records)' },
];

/**
 * v2, and the version bump is the whole point.
 *
 * The default below is being REVERSED, and this store holds which boxes are
 * folded — so anybody who has opened the dashboard since 8c has a saved value,
 * and a saved value beats a new default. Kyu has been on the live page. Left
 * on the old key he would have loaded this drop, seen all seven boxes still
 * open, and reasonably concluded it had not shipped.
 *
 * Bumping the key retires the old state instead of migrating it. Nothing is
 * lost that matters: the only thing forgotten is which box somebody folded by
 * hand yesterday, and the new default folds all of them anyway.
 */
const STORAGE_KEY = 'tap.dashboard.collapsed.v2';

/**
 * Pure, so the toggle logic is testable without a browser.
 */
export function toggleSection(collapsed: readonly string[], key: string): string[] {
  return collapsed.includes(key) ? collapsed.filter((entry) => entry !== key) : [...collapsed, key];
}

/** Every lower box, folded. The default, and the fallback for unreadable state. */
export function allCollapsed(): string[] {
  return DASHBOARD_SECTIONS.map((section) => section.key);
}

/**
 * Everything starts COLLAPSED. This reverses 8c, deliberately.
 *
 * 8c argued the opposite and the argument is kept here rather than deleted,
 * because it was not silly: the ask then was to make these foldable, not to
 * fold them, and a page of collapsed headings can read as broken rather than
 * tidy.
 *
 * Kyu overruled it from the live page, 2026-08-27: "These are secondary
 * inspection panels, not the primary dashboard surface." That is a statement
 * about what the page IS, and it outranks a guess about what a first load
 * looks like. The summary above them is unaffected — a summary you have to
 * unfold is not a summary, and none of it is foldable.
 *
 * Storage mirrors theme.ts and uiScale.ts: localStorage, per device, no
 * migration. Every read and write is guarded, because a browser with site
 * data blocked throws on access rather than returning nothing, and a
 * dashboard that will not render because it could not remember which box was
 * folded is a worse bug than the one being fixed.
 *
 * Note which way the guards fall now: unreadable, absent or corrupt state
 * returns EVERYTHING COLLAPSED, not everything open. The fallback has to be
 * the default, or a browser blocking site data would quietly serve one person
 * the page the other decision would have produced.
 */
export function readCollapsedSections(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return allCollapsed();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return allCollapsed();
    }
    const known = new Set(DASHBOARD_SECTIONS.map((section) => section.key));
    // Unknown keys are dropped rather than kept: a section that was renamed or
    // removed would otherwise sit in storage forever, and on the day its key
    // is reused it would come back folded for no reason anybody could explain.
    return parsed.filter((entry): entry is string => typeof entry === 'string' && known.has(entry));
  } catch {
    return allCollapsed();
  }
}

export function writeCollapsedSections(collapsed: readonly string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
  } catch {
    // Nothing to do and nothing worth saying. The page works either way; only
    // the memory of which box was folded is lost.
  }
}
